
import { defineComponent } from "vue";
import InputText from "primevue/inputtext";
import Password from "primevue/password";
import Button from "primevue/button";
import Panel from "primevue/panel";
import Message from "primevue/message";
import Dialog from "primevue/dialog";
import useVuelidate from "@vuelidate/core";
import { required, sameAs } from "@vuelidate/validators";
import Footer from "@/components/Footer.vue";
import LoginResponse from "@/types/response/login";
import { mapGetters } from "vuex";
import { useStatusService } from "@/composables/useStatusService";
import { useLoginService, LoginServiceComposable } from "@/composables/useLoginService";

enum ButtonText {
  Login = "Login",
  Register = "Register",
  UpdatePassword = "Update Password",
  RequestPasswordReset = "Request Password Reset",
  ResetPassword = "Reset Password",
}

export default defineComponent({
  name: "LoginForm",
  components: {
    InputText,
    Password,
    Button,
    Dialog,
    Panel,
    Footer,
    Message,
  },
  computed: {
    ...mapGetters({
      styles: "styles/getStyles"
    }),
    loginActive(): any {
      return {
        grey: this.buttonText !== ButtonText.Login,
        white: this.buttonText === ButtonText.Login,
      };
    },
    registerActive(): any {
      return {
        grey: this.buttonText !== ButtonText.Register,
        white: this.buttonText === ButtonText.Register,
      };
    },
    resetPasswordActive(): any {
      return {
        grey: this.buttonText !== ButtonText.RequestPasswordReset && this.buttonText !== ButtonText.ResetPassword,
        white: this.buttonText === ButtonText.RequestPasswordReset || this.buttonText === ButtonText.ResetPassword,
      };
    },
    allowPasswordReset(): boolean {
      return process.env.VUE_APP_ALLOW_PASSWORD_RESET === 'true';
    },
  },
  setup() {
    const { 
      isLoading: statusLoading, 
      error: statusError, 
      checkStatus 
    } = useStatusService();
    
    const { 
      isLoading: loginLoading, 
      error: loginError, 
      passwordError, 
      showPasswordValidationError,
      login,
      register,
      updatePassword,
      requestPasswordReset,
      portalResetPassword,
      resetPassword,
      encryptPassword 
    } = useLoginService();
    
    return { 
      v$: useVuelidate(),
      statusLoading,
      statusError,
      checkStatus,
      loginLoading,
      loginError,
      passwordError,
      showPasswordValidationError,
      login,
      register,
      updatePassword,
      requestPasswordReset,
      resetPassword,
      portalResetPassword,
      encryptPassword
    };
  },
  data() {
    return {
      ButtonTextOptions: ButtonText,
      Email: "",
      Password: "",
      OldPassword: "",
      submitted: false,
      buttonText: "Login",
      PasswordMatch: "",
      nonce: "",
      isResetPasswordFlow: false,
      infoMessage: "",
    };
  },
  async created() {
    await this.checkStatus();
    if (this.statusError) {
      this.loginError = this.statusError;
    }
    
    // Check for nonce in URL parameters for password reset flow
    // Format: ?nonce=<nonce>&email=<email>
    // This is used when user clicks the reset link from their email
    const urlParams = new URLSearchParams(window.location.search);
    const nonceParam = urlParams.get('nonce');
    const emailParam = urlParams.get('email');
    
    if (nonceParam) {
      this.nonce = nonceParam;
      if (emailParam) {
        this.Email = emailParam;
      }
      this.buttonText = ButtonText.ResetPassword;
      this.isResetPasswordFlow = true;
      this.infoMessage = "Please enter your new password.";
    }

    // After capturing sensitive reset parameters, remove them from the URL
    // to avoid leaking them via browser history, screenshots, or Referer.
    try {
      if (typeof window !== "undefined" && window.history && window.history.replaceState) {
        const currentUrl = new URL(window.location.href);
        const searchParams = currentUrl.searchParams;
        searchParams.delete("nonce");
        searchParams.delete("email");
        const remainingQuery = searchParams.toString();
        const newUrl =
          currentUrl.pathname +
          (remainingQuery ? "?" + remainingQuery : "") +
          currentUrl.hash;
        window.history.replaceState(null, document.title, newUrl);
      }
    } catch (e) {
      // If URL manipulation fails for any reason, continue without breaking the flow.
    }
    
    setInterval(async () => {
      await this.checkStatus();
      if (this.statusError) {
        this.loginError = this.statusError;
      }
    }, 240000);
  },
  validations() {
    return {
      Email: this.isResetPasswordFlow ? {} : { required },
      OldPassword: { required : this.buttonText === ButtonText.UpdatePassword },
      Password: { required: this.buttonText !== ButtonText.RequestPasswordReset },
      PasswordMatch: { sameAsPassword: sameAs(this.Password) },
    };
  },
  methods: {
    resetAuthFlow(buttonText: ButtonText) {
      this.buttonText = buttonText;
      this.Password = "";
      this.PasswordMatch = "";
      this.OldPassword = "";
      this.submitted = false;
      this.loginError = "";
      this.infoMessage = "";
      this.isResetPasswordFlow = false;
      this.nonce = "";
    },
    handleLogin(
      isFormValid: boolean,
      isEmailValid: boolean,
      isPasswordValid: boolean
    ) {
      this.submitted = true;
      if (this.buttonText === ButtonText.Register && isFormValid) {
        this.postRegister();
      }
      if (this.buttonText === ButtonText.Login && isEmailValid && isPasswordValid) {
        this.postLogin();
      }
      if (this.buttonText === ButtonText.UpdatePassword && isFormValid) {
        this.postUpdatePassword();
      }
      if (this.buttonText === ButtonText.RequestPasswordReset && isEmailValid) {
        this.postRequestPasswordReset();
      }
      if (this.buttonText === ButtonText.ResetPassword && isFormValid) {
        this.postResetPassword();
      }
      return;
    },
    async postRegister() {
      const response = await this.register(
        this.Email,
        this.Password,
        this.styles.client_name
      );

      if (response) {
        if (response.error) {
          this.loginError = response.error;
        } else {
          this.doLogin(response);
        }
      }
    },
    async postLogin() {
      const RESET_PASSWORD = "RESET_PASSWORD";
      
      const response = await this.login(
        this.Email,
        this.Password,
        this.styles.client_name
      );

      if (response) {
        if (response.error && response.action !== RESET_PASSWORD) {
          this.loginError = response.error;
          return;
        }
        
        if (response.action === RESET_PASSWORD) {
          this.buttonText = ButtonText.UpdatePassword;
          this.OldPassword = this.Password;
          this.Password = "";
          this.nonce = response.nonce;
          this.loginError = response.error || "You must update your password before proceeding.";
          return;
        }
        this.doLogin(response);
      }
    },
    async postUpdatePassword() {
      const RESET_PASSWORD = "RESET_PASSWORD";
      
      const response = await this.updatePassword(
        this.Email,
        this.OldPassword,
        this.Password,
        this.styles.client_name,
        this.nonce
      );

      if (response) {
        if (response.action === RESET_PASSWORD) {
          this.buttonText = ButtonText.UpdatePassword;
          this.nonce = response.nonce;
        }

        this.doLogin(response);
      }
    },
    async postRequestPasswordReset() {
      const response = await this.requestPasswordReset(
        this.Email,
        this.styles.client_name
      );

      if (response) {
        if (response.error) {
          this.loginError = response.error;
          this.infoMessage = "";
        }
        if (response.message) {
          this.infoMessage = response.message;
          this.loginError = "";
          this.submitted = false;
        }
      }
    },
    async postResetPassword() {
      const portalResetPassword = (this as unknown as Pick<LoginServiceComposable, 'portalResetPassword'>).portalResetPassword;
      const response = await portalResetPassword(
        this.Email,
        this.Password,
        this.styles.client_name,
        this.nonce
      );

      if (response) {
        if (response.error) {
          this.loginError = response.error;
          this.infoMessage = "";
        }
        if (response.message) {
          this.infoMessage = response.message;
          this.loginError = "";
        }
        // If no error, check if we can log the user in directly
        if (!response.error) {
          if (response.user || response.contact) {
            this.doLogin(response);
          } else {
            // Reset to login state
            this.buttonText = ButtonText.Login;
            this.Password = "";
            this.nonce = "";
            this.isResetPasswordFlow = false;
          }
        }
      }
    },
    handleLoginClick() {
      this.resetAuthFlow(ButtonText.Login);
    },
    handleRegisterClick() {
      this.resetAuthFlow(ButtonText.Register);
    },
    handleResetPasswordClick() {
      this.resetAuthFlow(ButtonText.RequestPasswordReset);
    },
    doLogin(response: LoginResponse) {
      const url = this.getUrl(response);

      if (response.user?.commands_rights && !Object.keys(response.user.commands_rights).length) {
        // this is a hack to reduce backend integrations work and maintain forward/backwards compatibility with new commands rights feature
        response.user.commands_rights = {};
        response.user.commands = [];
      }

      const payload = {
        session: btoa(
          JSON.stringify({
            name: this.styles.client_name,
            user: response.user ? response.user : response.contact,
            customers: response.custs,
            subKey: response.subscription_key,
            roverJwt: response.token,
            subscriptionClient: response.subscriptionClient,
            loginUrl: "" + process.env.VUE_APP_LOGIN_URL,
            theme: {
              primaryColor: this.styles.primary_color,
              backgroundColor: this.styles.background_color,
              secondaryColor: this.styles.secondary_color,
              sidebarTextColor: this.styles.sidebar_text_color,
              logo: this.styles.logo,
              customLogo: this.styles.custom_logo,
              title: this.styles.client_name,
              sidebarBGColor: this.styles.sidebar_bg_color,
              tabLogo: this.styles.tab_logo,
            },
          })
        ),
      };

      const paramString = new URLSearchParams(payload);
      location.replace(url + "?" + paramString);
    },
    getUrl(response: LoginResponse) {
      if (response.user) {
        return new URL("" + process.env.VUE_APP_WEB_CLIENT_URL);
      } else if (response.contact) {
        return new URL("" + process.env.VUE_APP_CUSTOMER_PORTAL_URL);
      } else {
        throw new Error("No valid user");
      }
    },
  },
});
